import Axios from 'axios'
import { createSimpleStore } from 'react-simple-reducer'

export interface TentativaSimulado {
  id: number
  createdAt: string
  pontuacao: number
  qtdQuestoes: number
  qtdQuestoesCorretas: number
  moduloNome: string | null
  modulos: Array<{ id: number; nome: string }>
}

export interface SimuladoDetalhes {
  avaliacao: {
    id: number
    avaliacoesQuestoes: Array<{ questaoId: number }>
  }
  modulosSelecionados: Array<{ id: number; nome: string }>
  questoes?: Array<{
    id: number
    textoQuestao: string
    percentualAcerto: number
  }>
}

export interface ResultadoSimulado {
  pontuacao: number
  qtdQuestoes: number
  qtdQuestoesCorretas: number
}

interface ResourceStatus {
  loading: boolean
  error: string
  lastFetchedAt: number | null
}

const defaultStatus: ResourceStatus = {
  loading: false,
  error: '',
  lastFetchedAt: null,
}
const TENTATIVAS_TTL_MS = 20_000

const parseErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ?? fallback

const shouldSkipResourceFetch = ({
  force,
  hasData,
  status,
}: {
  force: boolean
  hasData: boolean
  status: ResourceStatus
}) => {
  if (force) return false
  if (status.loading) return true
  if (hasData && status.lastFetchedAt) return true
  return false
}

const isFresh = (lastFetchedAt: number | null, ttlMs: number) => {
  if (!lastFetchedAt) return false
  return Date.now() - lastFetchedAt < ttlMs
}

const shouldSkipTentativasFetch = ({
  force,
  status,
}: {
  force: boolean
  status: ResourceStatus
}) => {
  if (force) return false
  if (status.loading) return true
  if (isFresh(status.lastFetchedAt, TENTATIVAS_TTL_MS)) return true
  return false
}

export const SimuladosStore = createSimpleStore(
  {
    tentativas: [] as TentativaSimulado[],
    tentativasStatus: { ...defaultStatus },
    resultadoByAvaliacaoId: {} as Record<number, ResultadoSimulado>,
    resultadoStatusByAvaliacaoId: {} as Record<number, ResourceStatus>,
    detalhesByAvaliacaoId: {} as Record<number, SimuladoDetalhes>,
    detalhesStatusByAvaliacaoId: {} as Record<number, ResourceStatus>,
  },
  {
    loadTentativasStarted(state) {
      state.tentativasStatus = {
        ...state.tentativasStatus,
        loading: true,
        error: '',
      }
    },
    loadTentativasSuccess(state, payload: { tentativas: TentativaSimulado[]; fetchedAt: number }) {
      state.tentativas = payload.tentativas
      state.tentativasStatus = {
        loading: false,
        error: '',
        lastFetchedAt: payload.fetchedAt,
      }
    },
    loadTentativasError(state, payload: { error: string }) {
      state.tentativasStatus = {
        ...state.tentativasStatus,
        loading: false,
        error: payload.error,
      }
    },
    clearTentativasError(state) {
      state.tentativasStatus = {
        ...state.tentativasStatus,
        error: '',
      }
    },
    invalidateTentativas(state) {
      state.tentativasStatus = {
        ...state.tentativasStatus,
        lastFetchedAt: null,
      }
    },
    loadResultadoStarted(state, payload: { avaliacaoId: number }) {
      state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        loading: true,
        error: '',
      }
    },
    loadResultadoSuccess(
      state,
      payload: { avaliacaoId: number; resultado: ResultadoSimulado; fetchedAt: number }
    ) {
      state.resultadoByAvaliacaoId[payload.avaliacaoId] = payload.resultado
      state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] = {
        loading: false,
        error: '',
        lastFetchedAt: payload.fetchedAt,
      }
    },
    loadResultadoError(state, payload: { avaliacaoId: number; error: string }) {
      state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        loading: false,
        error: payload.error,
      }
    },
    clearResultadoError(state, payload: { avaliacaoId: number }) {
      state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.resultadoStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        error: '',
      }
    },
    loadDetalhesStarted(state, payload: { avaliacaoId: number }) {
      state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        loading: true,
        error: '',
      }
    },
    loadDetalhesSuccess(
      state,
      payload: { avaliacaoId: number; detalhes: SimuladoDetalhes; fetchedAt: number }
    ) {
      state.detalhesByAvaliacaoId[payload.avaliacaoId] = payload.detalhes
      state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] = {
        loading: false,
        error: '',
        lastFetchedAt: payload.fetchedAt,
      }
    },
    loadDetalhesError(state, payload: { avaliacaoId: number; error: string }) {
      state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        loading: false,
        error: payload.error,
      }
    },
    clearDetalhesError(state, payload: { avaliacaoId: number }) {
      state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] = {
        ...(state.detalhesStatusByAvaliacaoId[payload.avaliacaoId] || defaultStatus),
        error: '',
      }
    },
  },
  {
    thunks: {
      loadTentativas(options?: { force?: boolean }) {
        return async (dispatch, getState) => {
          const force = !!options?.force
          const state = getState()
          const status = state.tentativasStatus || defaultStatus

          if (shouldSkipTentativasFetch({ force, status })) return

          dispatch({ type: 'loadTentativasStarted' })

          try {
            const tentativas = await Axios.get('/app/avaliacoes/simulados/tentativas').then(
              (x) => x.data
            )
            dispatch({
              type: 'loadTentativasSuccess',
              payload: { tentativas, fetchedAt: Date.now() },
            })
          } catch (error: any) {
            dispatch({
              type: 'loadTentativasError',
              payload: {
                error: parseErrorMessage(
                  error,
                  'Não foi possível carregar as tentativas de simulado'
                ),
              },
            })
          }
        }
      },
      loadResultado(avaliacaoId: number | string, options?: { force?: boolean }) {
        return async (dispatch, getState) => {
          const id = +avaliacaoId
          if (!id) return

          const force = !!options?.force
          const state = getState()
          const hasData = !!state.resultadoByAvaliacaoId[id]
          const status = state.resultadoStatusByAvaliacaoId[id] || defaultStatus

          if (shouldSkipResourceFetch({ force, hasData, status })) return

          dispatch({ type: 'loadResultadoStarted', payload: { avaliacaoId: id } })

          try {
            const resultado = await Axios.get('/app/avaliacoes/simulados/resultado', {
              params: { avaliacaoId: id },
            }).then((x) => x.data)

            dispatch({
              type: 'loadResultadoSuccess',
              payload: { avaliacaoId: id, resultado, fetchedAt: Date.now() },
            })
          } catch (error: any) {
            dispatch({
              type: 'loadResultadoError',
              payload: {
                avaliacaoId: id,
                error: parseErrorMessage(
                  error,
                  'Não foi possível carregar o resultado do simulado'
                ),
              },
            })
          }
        }
      },
      loadDetalhes(avaliacaoId: number | string, options?: { force?: boolean }) {
        return async (dispatch, getState) => {
          const id = +avaliacaoId
          if (!id) return

          const force = !!options?.force
          const state = getState()
          const hasData = !!state.detalhesByAvaliacaoId[id]
          const status = state.detalhesStatusByAvaliacaoId[id] || defaultStatus

          if (shouldSkipResourceFetch({ force, hasData, status })) return

          dispatch({ type: 'loadDetalhesStarted', payload: { avaliacaoId: id } })

          try {
            const detalhes = await Axios.get('/app/avaliacoes/simulados/detalhes', {
              params: { avaliacaoId: id },
            }).then((x) => x.data)

            dispatch({
              type: 'loadDetalhesSuccess',
              payload: { avaliacaoId: id, detalhes, fetchedAt: Date.now() },
            })
          } catch (error: any) {
            dispatch({
              type: 'loadDetalhesError',
              payload: {
                avaliacaoId: id,
                error: parseErrorMessage(error, 'Não foi possível carregar o gabarito.'),
              },
            })
          }
        }
      },
      loadGabarito(avaliacaoId: number | string, options?: { force?: boolean }) {
        return async (dispatch, getState) => {
          const id = +avaliacaoId
          if (!id) return

          await Promise.allSettled([
            SimuladosStore.thunks.loadDetalhes(id, options)(dispatch as any, getState as any),
            SimuladosStore.thunks.loadResultado(id, options)(dispatch as any, getState as any),
          ])
        }
      },
    },
  }
)

export const getStatusByAvaliacaoId = (
  map: Record<number, ResourceStatus>,
  avaliacaoId: number
): ResourceStatus => {
  return map[avaliacaoId] || defaultStatus
}

export const getResultadoByAvaliacaoId = (
  map: Record<number, ResultadoSimulado>,
  avaliacaoId: number
) => {
  return map[avaliacaoId]
}

export const getDetalhesByAvaliacaoId = (
  map: Record<number, SimuladoDetalhes>,
  avaliacaoId: number
) => {
  return map[avaliacaoId]
}
